'use client'

import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { CheckCircle, Lock } from '@mui/icons-material'
import {
  Card,
  Stack,
  Typography,
  Box,
  CardMedia,
  Paper,
  Divider,
  Tooltip,
  Chip,
} from '@mui/material'
import type { SxProps } from '@mui/material'
import { alpha } from '@mui/material/styles'

import Image from 'next/image'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { getTranslatedTagLabel } from '../../utils/tagTranslation'
import SupplierDeliverySummary from '../delivery-summary/supplier-delivery-summary'
import { SupplierRating } from '../supplier-rating'

export interface VendorTag {
  name: string | null
  label: string | null
  groupId: string | null
}

interface SupplierCardCardProps {
  companyName: string
  logo?: string | null
  image?: {
    secureUrl: string
    alt?: string | null
  } | null
  rating?: number | null
  highlightName?: string
  width?: number | string
  mediaHeight?: number
  mediaAspectRatio?: number
  minRatingToShow?: number
  sx?: SxProps
  favorite?: React.ReactNode
  locationDetails?: React.ReactNode
  description?: React.ReactNode
  privateItem?: boolean
  approvedVendor?: boolean
  tags?: (VendorTag | null)[] | null
  showRating?: boolean
  vendor: {
    id: string
    currency: string | null
  }
  deliverySummary?: {
    deliveryFee?: number | null
    minOrderFee?: number | null
    minNoticePeriod?: number | null
  }
  totalRatings?: number | null
  itemSearchMetadata?: {
    rank: number
    reason: string
    items: Array<{ id: string; name: string }>
  } | null
}

const renderFavouriteAndRating = ({
  favorite,
  effectiveRating,
  minRatingToShow,
  highlightName,
  totalRatings,
  showRating,
}: {
  favorite?: React.ReactNode
  effectiveRating: number
  minRatingToShow: number
  highlightName?: string
  totalRatings?: number | null
  showRating?: boolean
}) => {
  const canShowRating = showRating ?? effectiveRating >= minRatingToShow
  const hasRightControls = Boolean(favorite) || canShowRating
  if (highlightName || !hasRightControls) return null
  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
      {favorite && (
        <Box
          sx={{
            backgroundColor: 'grey.200',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: canShowRating ? 8 : 0,
            borderTopLeftRadius: 0,
            borderTopRightRadius: canShowRating ? 0 : 8,
            cursor: 'pointer',
          }}>
          {favorite}
        </Box>
      )}

      {canShowRating && (
        <Box
          sx={{
            backgroundColor: 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 0.5,
            borderBottomLeftRadius: 8,
            borderTopRightRadius: 8,
          }}>
          <SupplierRating rating={effectiveRating} totalRatings={totalRatings} />
        </Box>
      )}
    </Box>
  )
}

const SupplierCard: React.FC<SupplierCardCardProps> = ({
  companyName,
  logo,
  image,
  rating = 0,
  highlightName,
  width,
  mediaHeight,
  mediaAspectRatio = 16 / 9,
  minRatingToShow = 2,
  sx,
  favorite,
  vendor,
  totalRatings,
  description,
  privateItem,
  approvedVendor,
  tags,
  deliverySummary,
  itemSearchMetadata,
  showRating,
}) => {
  const { t } = useTranslation()
  const showMatchingItems = useFeatureFlag(SPLITS.GM_ITEM_SEARCH_MATCHING_ITEMS)

  const effectiveRating = rating ?? 0
  const canShowHighlightRating = showRating ?? effectiveRating >= minRatingToShow
  const tagItems = useMemo(() => (tags ?? []).filter((tag) => tag?.label), [tags])
  const hasTags = tagItems.length > 0
  const hasHighlight = Boolean(highlightName)
  const safeAspectRatio = mediaAspectRatio && mediaAspectRatio > 0 ? mediaAspectRatio : 16 / 9
  const cardWidth = width ?? '100%'
  const baseSx: SxProps = {
    position: 'relative',
    width: cardWidth,
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  }
  const rootSx: SxProps = sx ? (Array.isArray(sx) ? [baseSx, ...sx] : [baseSx, sx]) : baseSx

  return (
    <Box sx={rootSx}>
      <Card
        sx={{
          width: '100%',
          borderRadius: 2,
          flexShrink: 0,
        }}>
        <CardMedia
          component="div"
          sx={{
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            ...(mediaHeight
              ? { height: mediaHeight }
              : {
                  pt: `${100 / safeAspectRatio}%`,
                }),
          }}>
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
            }}>
            {image?.secureUrl && (
              <Image
                src={image.secureUrl}
                alt={image.alt || `${companyName} image`}
                fill
                style={{
                  objectFit: 'cover',
                }}
                loading="lazy"
                sizes="300px"
              />
            )}
          </Box>
        </CardMedia>
        {logo && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              zIndex: 4,
            }}>
            <Image
              src={logo}
              width={60}
              height={60}
              alt={`${companyName} logo`}
              style={{
                borderRadius: '50%',
                objectFit: 'cover',
                backgroundColor: 'white',
                filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
              }}
            />
          </Box>
        )}
        {privateItem && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'absolute',
              top: approvedVendor ? 30 : 0,
              right: 0,
              zIndex: 4,
              borderTopLeftRadius: approvedVendor ? 8 : 0,
              borderBottomLeftRadius: 8,
              borderTopRightRadius: approvedVendor ? 0 : 8,
              backgroundColor: 'primary.main',
              color: 'primary.contrastText',
              px: 1,
              py: 0.5,
              fontSize: 12,
              fontWeight: 700,
              gap: 1,
            }}>
            <Lock fontSize={'inherit'} />
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: 'primary.contrastText',
              }}>
              {t('marketplace:privateItems')}
            </Typography>
          </Box>
        )}
        {approvedVendor && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              position: 'absolute',
              top: 0,
              right: 0,
              zIndex: 4,
              borderTopRightRadius: 8,
              borderBottomLeftRadius: 8,
              backgroundColor: 'inverted.contrastText',
              color: 'inverted.main',
              px: 1,
              py: 0.5,
              fontSize: 12,
              fontWeight: 700,
              gap: 1,
            }}>
            <CheckCircle fontSize={'inherit'} />
            <Typography
              component="span"
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: 'inverted.main',
              }}>
              {t('marketplace:approvedVendor')}
            </Typography>
          </Box>
        )}
      </Card>
      <Paper
        sx={{
          marginTop: -4,
          marginLeft: 1,
          marginRight: 1,
          borderRadius: 2,
          boxShadow: '0px 3.44px 9.46px 0px #0000001A',
          position: 'relative',
          justifyContent: 'space-between',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
        }}>
        <Box>
          {highlightName && canShowHighlightRating && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 0.5,
                borderBottomRightRadius: 8,
                borderTopLeftRadius: 8,
              }}>
              <SupplierRating rating={effectiveRating} />
            </Box>
          )}{' '}
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'start',
                gap: 1,
                justifyContent: 'space-between',
              }}>
              <Box px={1} py={0.5} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant={highlightName ? 'h6' : 'h5'} title={companyName}>
                  {companyName}
                </Typography>
              </Box>
              {renderFavouriteAndRating({
                favorite,
                effectiveRating,
                minRatingToShow,
                highlightName,
                totalRatings,
                showRating,
              })}
            </Box>
          </Box>
          <Box px={1}>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
            <Divider
              sx={{
                my: 0.5,
              }}
            />
            <SupplierDeliverySummary
              currency={vendor.currency}
              deliveryFee={deliverySummary?.deliveryFee}
              minOrderFee={deliverySummary?.minOrderFee}
              minNoticePeriod={deliverySummary?.minNoticePeriod}
            />
          </Box>
        </Box>
        {(hasTags || hasHighlight) && (
          <Stack spacing={1} p={1}>
            {hasTags && (
              <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} py={1} fontSize={10}>
                {tagItems.map((tag) => (
                  <Tooltip
                    key={`${tag?.groupId ?? 'tag'}-${tag?.label ?? ''}-${tag?.name ?? ''}`}
                    placement="top"
                    title={
                      tag?.label
                        ? t('marketplace:tagTooltip', { tag: tag.label }) ||
                          `This is a ${tag.label}`
                        : ''
                    }>
                    <Chip
                      label={
                        <Typography variant="caption" color="primary.main">
                          {tag
                            ? getTranslatedTagLabel(
                                {
                                  label: tag.label || '',
                                  groupId: tag.groupId,
                                },
                                t,
                              )
                            : ''}
                        </Typography>
                      }
                      color="primary"
                      variant="outlined"
                      size="small"
                    />
                  </Tooltip>
                ))}
              </Stack>
            )}
            {hasHighlight && (
              <Stack direction="column" spacing={1}>
                <Typography
                  variant="caption"
                  color="primary.main"
                  noWrap
                  title={highlightName || undefined}
                  sx={{ maxWidth: '100%' }}>
                  {highlightName}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
        {itemSearchMetadata && (
          <Box
            sx={(theme) => ({
              px: 2,
              pt: 1.25,
              pb: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
              borderBottomLeftRadius: theme.shape.borderRadius,
              borderBottomRightRadius: theme.shape.borderRadius,
            })}>
            <Chip
              label={`#${itemSearchMetadata.rank} MATCH`}
              color="primary"
              variant="filled"
              size="small"
              sx={{
                alignSelf: 'flex-start',
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: 0.5,
              }}
            />

            <Typography
              variant="caption"
              fontStyle="italic"
              sx={(theme) => ({
                color: theme.palette.primary.main,
                lineHeight: 1.4,
              })}>
              {itemSearchMetadata.reason}
            </Typography>

            {showMatchingItems && itemSearchMetadata.items.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    color: theme.palette.primary.main,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    lineHeight: 1.4,
                  })}>
                  Matching Items
                </Typography>
                <Box
                  component="ul"
                  sx={{ m: 0, mt: 0.5, pl: 2.5, maxHeight: 120, overflowY: 'auto' }}>
                  {itemSearchMetadata.items.map((item) => (
                    <Box component="li" key={item.id}>
                      <Typography sx={{ lineHeight: 1.4 }} variant="caption">
                        {item.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default SupplierCard
